import { supabase } from '../supabaseClient';
import { buildOrderCode, formatMoney } from '../admin/ordersPage';
import {
  REFUND_REQUEST_ISSUE_TYPE,
  SUPPORT_TICKET_STATUSES,
  isRefundRequestType,
} from './supportTickets';

export { REFUND_REQUEST_ISSUE_TYPE, isRefundRequestType };

export const formatRefundOrderOptionLabel = ({ orderCode, eventTitle, eventDate, amount, usedHint }) => {
  const parts = [
    orderCode || '#ORD-???',
    eventTitle || 'Event',
    eventDate || null,
    formatMoney(amount),
  ].filter(Boolean);

  const base = parts.join(' · ');
  return usedHint ? `${base} (tickets used — admin review)` : base;
};

export const filterEligibleRefundOrders = (orders = [], openRequestOrderIds = []) => {
  const blocked = new Set((openRequestOrderIds || []).map((id) => String(id)));

  return (orders || []).filter((order) => {
    const status = String(order?.status || '').toLowerCase();
    if (status !== 'paid') return false;
    if (blocked.has(String(order.id))) return false;
    return true;
  });
};

export const listBuyerPaidOrdersForRefund = async (client = supabase) => {
  const {
    data: { user },
    error: authError,
  } = await client.auth.getUser();

  if (authError) {
    throw new Error(authError.message || 'Unable to confirm your account.');
  }
  if (!user?.id) {
    throw new Error('Please sign in to request a refund.');
  }

  const { data: orders, error: ordersError } = await client
    .from('payment_orders')
    .select('id, amount, status, paid_at, created_at, event_id')
    .eq('user_id', user.id)
    .eq('status', 'paid')
    .order('paid_at', { ascending: false });

  if (ordersError) {
    throw new Error(ordersError.message || 'Unable to load your paid orders.');
  }

  const paidOrders = Array.isArray(orders) ? orders : [];
  if (!paidOrders.length) return [];

  const orderIds = paidOrders.map((order) => order.id);
  const eventIds = [...new Set(paidOrders.map((order) => order.event_id).filter(Boolean))];

  const [{ data: events }, { data: openRequests }, { data: tickets }] = await Promise.all([
    eventIds.length
      ? client.from('events').select('id, title, date').in('id', eventIds)
      : Promise.resolve({ data: [] }),
    client
      .from('support_tickets')
      .select('payment_order_id')
      .eq('user_id', user.id)
      .eq('issue_type', REFUND_REQUEST_ISSUE_TYPE)
      .in('status', [SUPPORT_TICKET_STATUSES.OPEN, SUPPORT_TICKET_STATUSES.PENDING])
      .in('payment_order_id', orderIds),
    client
      .from('tickets')
      .select('payment_order_id, is_validated')
      .in('payment_order_id', orderIds),
  ]);

  const eventsById = {};
  (events || []).forEach((event) => {
    eventsById[event.id] = event;
  });

  const openRequestOrderIds = (openRequests || [])
    .map((row) => row.payment_order_id)
    .filter(Boolean);

  const usedByOrder = new Map();
  (tickets || []).forEach((ticket) => {
    if (!ticket.payment_order_id) return;
    if (ticket.is_validated) {
      usedByOrder.set(ticket.payment_order_id, true);
    }
  });

  const eligible = filterEligibleRefundOrders(paidOrders, openRequestOrderIds);

  return eligible.map((order) => {
    const event = eventsById[order.event_id];
    const orderCode = buildOrderCode(order.id);
    const usedHint = Boolean(usedByOrder.get(order.id));

    return {
      id: order.id,
      orderCode,
      amount: Number(order.amount || 0),
      eventTitle: event?.title || 'Event',
      eventDate: event?.date || '',
      usedHint,
      label: formatRefundOrderOptionLabel({
        orderCode,
        eventTitle: event?.title || 'Event',
        eventDate: event?.date || '',
        amount: order.amount,
        usedHint,
      }),
    };
  });
};

export const listOpenRefundRequestsForOrders = async (orderIds = [], client = supabase) => {
  const ids = (orderIds || []).filter(Boolean);
  if (!ids.length) return [];

  const { data, error } = await client
    .from('support_tickets')
    .select('id, ticket_code, issue_type, subject, description, status, payment_order_id, admin_notes, created_at, updated_at')
    .eq('issue_type', REFUND_REQUEST_ISSUE_TYPE)
    .in('status', [SUPPORT_TICKET_STATUSES.OPEN, SUPPORT_TICKET_STATUSES.PENDING])
    .in('payment_order_id', ids)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Unable to load refund requests.');
  }

  return Array.isArray(data) ? data : [];
};

export const markOrderRefunded = async (orderId, adminNote = '', client = supabase) => {
  const trimmedId = String(orderId || '').trim();
  if (!trimmedId) {
    throw new Error('Order id is required.');
  }

  const { data, error } = await client.rpc('mark_payment_order_refunded', {
    p_order_id: trimmedId,
    p_admin_note: String(adminNote || '').trim() || null,
  });

  if (error) {
    throw new Error(error.message || 'Unable to mark this order as refunded.');
  }

  return data;
};

export const denyRefundRequest = async (supportTicketId, adminNote = '', client = supabase) => {
  const trimmedId = String(supportTicketId || '').trim();
  const note = String(adminNote || '').trim();
  if (!trimmedId) {
    throw new Error('Support ticket id is required.');
  }
  if (!note) {
    throw new Error('A deny note is required.');
  }

  const { data, error } = await client.rpc('deny_refund_request', {
    p_support_ticket_id: trimmedId,
    p_admin_note: note,
  });

  if (error) {
    throw new Error(error.message || 'Unable to deny this refund request.');
  }

  return data;
};
